import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fallbackArticles = [
  { id: "1", title: "ISO 27001:2022 Transition — What Organizations Need to Know", category: "Standards Update", summary: "The transition deadline for ISO 27001:2022 is approaching. Current 2013-certificates must be transitioned before the deadline.", content: "## Key Transition Requirements\n\nOrganizations currently certified to **ISO 27001:2013** must transition to the **2022 revision** before the deadline.\n\n### What Changed\n- Annex A controls reorganized from 14 categories to 4 themes\n- 11 new controls added including *threat intelligence* and *cloud security*\n- Greater emphasis on **risk-based thinking**\n\n### Timeline\n1. Gap analysis against new requirements\n2. Update your Statement of Applicability\n3. Implement new and modified controls\n4. Schedule transition audit with your certification body", created_at: "2025-12-15T10:00:00Z", author: "GACA Editorial", image_url: "" },
  { id: "2", title: "The Rising Threat of AI-Enabled Cyber Attacks in 2026", category: "Cybersecurity", summary: "As AI-linked cyber incidents rise, organizations must adapt their ISMS to address evolving threats.", content: "## AI and Cybersecurity\n\nArtificial intelligence is transforming the threat landscape. Attackers are leveraging AI for:\n\n- **Automated phishing** campaigns with realistic content\n- **Deepfake** audio and video for social engineering\n- **Intelligent malware** that adapts to defenses\n\n### Defensive Measures\nOrganizations should update their ISMS to include:\n1. AI-specific threat assessments\n2. Enhanced employee awareness training\n3. Automated detection and response capabilities", created_at: "2025-12-08T10:00:00Z", author: "GACA Editorial", image_url: "" },
  { id: "3", title: "SOC 2 vs ISO 27001: Choosing the Right Framework", category: "Compliance", summary: "A comparison to help organizations choose the appropriate compliance framework.", content: "## Framework Comparison\n\n| Aspect | SOC 2 | ISO 27001 |\n|--------|-------|----------|\n| Origin | AICPA (US) | ISO (International) |\n| Scope | Trust Service Criteria | ISMS |\n| Recognition | Primarily North America | Global |\n\n### When to Choose SOC 2\n- Your clients are primarily US-based\n- You need to demonstrate controls over a reporting period\n\n### When to Choose ISO 27001\n- You operate internationally\n- You need a **certified management system**\n- Regulatory requirements specify ISO alignment", created_at: "2025-12-01T10:00:00Z", author: "GACA Editorial", image_url: "" },
  { id: "4", title: "Building Organizational Resilience with ISO 22301", category: "Business Continuity", summary: "ISO 22301 helps organizations prepare for, respond to, and recover from disruptive incidents.", created_at: "2025-11-25T10:00:00Z", author: "GACA Editorial", image_url: "" },
  { id: "5", title: "Why Every Organization Needs a Certified ISMS", category: "Certification", summary: "ISO 27001 certification demonstrates to clients and regulators that your organization takes information security seriously.", created_at: "2025-11-18T10:00:00Z", author: "GACA Editorial", image_url: "" },
];

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-lg font-bold text-[#0B2545] mt-6 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-sm font-bold text-[#0B2545] mt-4 mb-2">{line.slice(4)}</h3>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(<li key={i} className="text-sm text-slate-600 leading-relaxed">{renderInline(lines[i].slice(2))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-disc list-inside space-y-1 mb-3 ml-2">{items}</ul>);
      continue;
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(<li key={i} className="text-sm text-slate-600 leading-relaxed">{renderInline(lines[i].replace(/^\d+\.\s/, ''))}</li>);
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="list-decimal list-inside space-y-1 mb-3 ml-2">{items}</ol>);
      continue;
    } else if (line.startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      if (tableLines.length >= 2) {
        const headerCells = tableLines[0].split('|').filter(c => c.trim());
        const bodyRows = tableLines.slice(2);
        elements.push(
          <div key={`table-${i}`} className="overflow-x-auto mb-4 mt-2">
            <table className="w-full text-xs border border-[#0B2545]/15">
              <thead><tr className="bg-[#0B2545] text-white">{headerCells.map((c, ci) => <th key={ci} className="px-3 py-2 text-left font-bold">{c.trim()}</th>)}</tr></thead>
              <tbody>{bodyRows.map((row, ri) => {
                const cells = row.split('|').filter(c => c.trim());
                return <tr key={ri} className={ri % 2 ? 'bg-[#F7F9FC]' : 'bg-white'}>{cells.map((c, ci) => <td key={ci} className="px-3 py-2 text-slate-600 border-t border-[#0B2545]/10">{c.trim()}</td>)}</tr>;
              })}</tbody>
            </table>
          </div>
        );
      }
      continue;
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(<p key={i} className="text-sm text-slate-600 leading-relaxed mb-2">{renderInline(line)}</p>);
    }
    i++;
  }
  return elements;
}

function renderInline(text) {
  const parts = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    const italicMatch = remaining.match(/\*(.+?)\*/);
    const match = boldMatch && (!italicMatch || boldMatch.index <= italicMatch.index) ? boldMatch : italicMatch;
    if (!match) { parts.push(remaining); break; }
    if (match.index > 0) parts.push(remaining.slice(0, match.index));
    if (match[0].startsWith('**')) {
      parts.push(<strong key={key++} className="font-bold text-[#0B2545]">{match[1]}</strong>);
    } else {
      parts.push(<em key={key++} className="italic text-[#13A8BD]">{match[1]}</em>);
    }
    remaining = remaining.slice(match.index + match[0].length);
  }
  return parts;
}

export default function InsightsPage() {
  const [articles, setArticles] = useState(fallbackArticles);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const { t } = useLang();
  const ip = t?.insights_page || {};

  useEffect(() => {
    axios.get(`${API}/blog`).then((res) => {
      if (res.data.length > 0) setArticles(res.data);
    }).catch(() => {});
  }, [setArticles]);

  const categories = ["All", ...new Set(articles.map(a => a.category).filter(Boolean))];

  const filtered = articles.filter(a => {
    const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.summary.toLowerCase().includes(search.toLowerCase()) || (a.category || "").toLowerCase().includes(search.toLowerCase());
    const matchCat = activeCategory === "All" || a.category === activeCategory;
    return matchSearch && matchCat;
  });

  const featured = !search && activeCategory === "All" ? filtered[0] : null;
  const rest = featured ? filtered.slice(1) : filtered;

  return (
    <div className="page-transition pt-20" data-testid="insights-page">
      <section className="bg-[#0B2545] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#13A8BD] mb-4">{ip.badge || "Insights"}</p>
          <h1 data-testid="insights-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
            {ip.title || "Our Thinking"}
          </h1>
          <p className="text-base md:text-lg text-white/65 max-w-2xl leading-relaxed">
            {ip.subtitle || "Expert perspectives on certification standards, cybersecurity trends, and compliance best practices."}
          </p>
        </div>
      </section>

      {/* Search + Filter */}
      <section className="bg-[#F7F9FC] border-b border-[#0B2545]/10 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={ip.search_placeholder || "Search articles..."}
                data-testid="insights-search-input"
                className="w-full pl-10 pr-10 py-2.5 bg-white border border-[#0B2545]/15 text-sm text-[#0B2545] placeholder:text-slate-400 focus:outline-none focus:border-[#13A8BD]"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-[#0B2545]">
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex gap-1 flex-wrap" data-testid="insights-category-filters">
              {categories.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)} data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                    activeCategory === cat ? 'bg-[#0B2545] text-white' : 'bg-white text-slate-500 border border-[#0B2545]/10 hover:border-[#13A8BD] hover:text-[#13A8BD]'
                  }`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>
          {search && <p className="mt-3 text-xs text-slate-500">{filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"</p>}
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="py-12 bg-white border-b border-[#0B2545]/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="border border-[#0B2545]/10 hover:bg-[#F7F9FC] transition-colors cursor-pointer" onClick={() => setExpandedId(expandedId === featured.id ? null : featured.id)}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
                <div className="lg:col-span-2 p-8 lg:p-12">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[10px] font-bold text-[#13A8BD] uppercase tracking-wider">Featured</span>
                    <span className="text-[10px] text-slate-400">|</span>
                    <span className="text-[10px] text-slate-400">{new Date(featured.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#0B2545] mb-4 leading-snug" style={{ fontFamily: "Chivo, sans-serif" }}>{featured.title}</h2>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3">{featured.summary}</p>
                  <span className="text-[10px] font-bold text-[#13A8BD] uppercase tracking-wider">{expandedId === featured.id ? 'Close' : (ip.read_more || 'Read More')}</span>
                </div>
                <div className="hidden lg:block">
                  {featured.image_url ? (
                    <img src={featured.image_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full bg-gradient-to-br from-[#0B2545] to-[#13A8BD]/30 flex items-center justify-center p-8">
                      <span className="text-6xl font-black text-white/10" style={{ fontFamily: "Chivo, sans-serif" }}>ISO</span>
                    </div>
                  )}
                </div>
              </div>
              {expandedId === featured.id && featured.content && (
                <div className="px-8 lg:px-12 pb-8 lg:pb-12 border-t border-[#0B2545]/10" data-testid="featured-article-content">
                  <div className="max-w-3xl pt-6">{renderMarkdown(featured.content)}</div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Articles */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {rest.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-slate-500">No articles found{search ? ` matching "${search}"` : ''}.</p>
            </div>
          ) : (
            <div className="space-y-0 border border-[#0B2545]/10">
              {rest.map((article, i) => (
                <div key={article.id || i} data-testid={`insight-article-${i}`}
                  className="border-b border-[#0B2545]/10 last:border-b-0 hover:bg-[#F7F9FC] transition-colors">
                  <div className="p-6 lg:p-8 cursor-pointer" onClick={() => setExpandedId(expandedId === article.id ? null : article.id)}>
                    <div className="flex items-start gap-4">
                      {article.image_url && (
                        <img src={article.image_url} alt="" className="w-20 h-20 object-cover flex-shrink-0 border border-[#0B2545]/10 hidden md:block" onError={(e) => { e.target.style.display='none'; }} />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[10px] text-slate-400">{new Date(article.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className="text-[10px] font-bold text-[#13A8BD] uppercase tracking-wider">{article.category}</span>
                        </div>
                        <h3 className="text-base font-bold text-[#0B2545] mb-1.5 leading-snug" style={{ fontFamily: "Chivo, sans-serif" }}>
                          {article.title}
                        </h3>
                        <p className="text-xs text-slate-500 leading-relaxed mb-2">{article.summary}</p>
                        <div className="flex items-center gap-4">
                          <span className="text-[10px] text-slate-400">{article.author}</span>
                          <span className="text-[10px] font-bold text-[#13A8BD] uppercase tracking-wider">{expandedId === article.id ? 'Close' : (ip.read_more || 'Read More')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedId === article.id && article.content && (
                    <div className="px-6 lg:px-8 pb-6 lg:pb-8 border-t border-[#0B2545]/10" data-testid={`article-content-${i}`}>
                      <div className="max-w-3xl pt-6">{renderMarkdown(article.content)}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-12 bg-[#0B2545]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Stay Informed</h2>
          <p className="text-xs text-white/50 mb-4">Subscribe for the latest certification standards and cybersecurity insights.</p>
          <Link to="/contact" data-testid="insights-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
